import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MembersCourse } from '../../../../../models/interfaces/members_course.interface';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomFile } from '../../../../../models/interfaces/custom_file.interface';
import { RemoteFile } from '../../../../../models/classes/remote_file.model';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { UtilsService } from '../../../../../shared/utils/utils.service';
import { v4 as uuidv4 } from 'uuid';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EnfafDropzoneComponent } from '../../../../../components/enfaf-dropzone/enfaf-dropzone.component';
import { EnfafRemoteAttachmentsComponent } from '../../../../../components/enfaf-remote-attachments/enfaf-remote-attachments.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
    selector: 'app-modal-edit-members-data',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        EnfafDropzoneComponent,
        EnfafRemoteAttachmentsComponent,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatDialogModule,
        MatProgressSpinnerModule,
        MatSlideToggleModule
    ],
    templateUrl: './modal-edit-members-data.component.html',
    styleUrl: './modal-edit-members-data.component.scss'
})
export class ModalEditMembersDataComponent implements OnInit, OnDestroy {
  isLoadingData = true;
  receivedCourse!: MembersCourse;
  membersForm: FormGroup = new FormGroup({});
  editMode = false;
  showCloseAlert = false;
  isSavingMembers = false;
  imageToUpload: File | null = null;
  imageToUploadSrc: string | null = null;
  currentThumbnail!: string;
  filesToUpload: Array<CustomFile> = [];
  currentAttachments: Array<RemoteFile> = [];
  isDeletingRemoteAttachment = false;

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalEditMembersDataComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService,
    private utils: UtilsService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.fetchMembersBasicData();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }
    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  async fetchMembersBasicData(): Promise<void> {
    try {
      const response = await firstValueFrom(this.api.fetchMembersBasicData());
      this.isLoadingData = false;
      this.receivedCourse = response.course;
      this.initForm();
    } catch (errorResponse: any) {
      this.isLoadingData = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error buscando información');
    }
  }

  initForm(): void {
    if (this.receivedCourse) {
      this.editMode = true;
      this.currentThumbnail = this.receivedCourse.thumbnailUrl;
      this.currentAttachments = this.receivedCourse.attachments;
    }
    this.membersForm = new FormGroup({
      membersId: new FormControl(this.receivedCourse?.membersId || uuidv4(), Validators.required),
      name: new FormControl(this.receivedCourse?.name || null, Validators.required),
      hidden: new FormControl(this.receivedCourse?.hidden || false, Validators.required)
    });
    this.listenForChanges();
  }

  listenForChanges(): void {
    this.membersForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.showCloseAlert = true;
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
    });
  }

  async onSelectImage(event: any) {
    try {
      const imgFile: File = event.target.files && event.target.files[0];
      if (!imgFile) return;
      if (imgFile.size > this.utils.maxImageSize) {
        (document.getElementById('membersThumbnail') as HTMLInputElement).value = '';
        const fileName = imgFile.name;
        const errorMessage = `La imagen '${fileName}' no puede exceder los ${this.utils.maxImageSize / this.utils.oneMegaByte} MB\n`;
        this.toast.presentErrorToast(errorMessage);
        return;
      }
      this.imageToUploadSrc = await this.utils.previewImageFile(imgFile);
      if (!this.imageToUploadSrc) return;
      this.imageToUpload = imgFile;
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      this.toast.presentErrorToast('Hubo un error adjuntando imagen');
    }
  }

  onDroppedAttachments(files: Array<CustomFile>): void {
    if (!files?.length) return;
    for (const file of files) {
      file.name = this.utils.generateFileName(file.name);
    }
    this.filesToUpload = files;
  }

  async saveMembers(): Promise<void> {
    try {
      this.isSavingMembers = true;
      const membersFormData = new FormData();
      const formValue = this.membersForm.value;
      membersFormData.append('form', JSON.stringify(formValue));
      if (this.imageToUpload) {
        membersFormData.append('thumbnail', this.imageToUpload, this.imageToUpload.name);
      }
      if (this.filesToUpload.length) {
        for (const [fileIndex, fileValue] of this.filesToUpload.entries()) {
          membersFormData.append(`attachment_${fileIndex}`, fileValue.file, fileValue.name);
        }
      }
      const response = await firstValueFrom(this.api.saveMembersData(membersFormData));
      this.isSavingMembers = false;
      this.toast.presentSuccessToast(response.message);
      this.closeModal({ newName: response.newName });
    } catch (errorResponse: any) {
      this.isSavingMembers = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error guardando clase');
    }
  }

  async onDeleteRemoteAttachment(result: { attachment: RemoteFile; index: number }): Promise<void> {
    try {
      const { attachment, index } = result;
      this.isDeletingRemoteAttachment = true;
      const membersId = this.membersForm.value.membersId;
      const body = {
        membersId,
        attachment
      };
      const response = await firstValueFrom(this.api.deleteMembersDataAttachment(body));
      this.isDeletingRemoteAttachment = false;
      this.currentAttachments.splice(index, 1);
      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      this.isDeletingRemoteAttachment = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error borrando adjunto');
    }
  }
}
